import { useEffect, useState } from 'react'
import { useAlert } from '../contexts/AlertContext'
import { attendancePing } from '../lib/apiPhpAdapter'
import { AttendanceCredentialsPanel } from './attendance/AttendanceCredentialsPanel'
import { AttendanceHoursSettingsPanel } from './attendance/AttendanceHoursSettingsPanel'
import { AttendanceTerminalsPanel } from './attendance/AttendanceTerminalsPanel'
import './AttendanceView.css'
import './attendance/AttendanceOrgAdmin.css'

type TabKey = 'terminals' | 'credentials' | 'settings'

type NavEntry =
  | { kind: 'group'; label: string }
  | { kind: 'item'; key: TabKey; label: string }

const NAV: NavEntry[] = [
  { kind: 'group', label: 'Zařízení' },
  { kind: 'item', key: 'terminals', label: 'Terminály' },
  { kind: 'item', key: 'credentials', label: 'Čip / karta / otisk' },
  { kind: 'group', label: 'Systém' },
  { kind: 'item', key: 'settings', label: 'Nastavení' },
]

const TAB_TITLES: Record<TabKey, string> = {
  terminals: 'Terminály',
  credentials: 'Čip / karta / otisk',
  settings: 'Nastavení',
}

export default function AttendanceView() {
  const { showAlert } = useAlert()
  const [tab, setTab] = useState<TabKey>('terminals')
  const [pingOk, setPingOk] = useState<boolean | null>(null)
  const [pingMsg, setPingMsg] = useState('')
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setBusy(true)
      try {
        const res = await attendancePing()
        if (cancelled) return
        setPingOk(true)
        setPingMsg(res?.message || 'OK')
      } catch (e: unknown) {
        if (cancelled) return
        setPingOk(false)
        setPingMsg(e instanceof Error ? e.message : 'Ping failed')
        showAlert('Nepodařilo se připojit k modulu docházky.', 'error')
      } finally {
        if (!cancelled) setBusy(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [showAlert])

  return (
    <div className="attendance-root">
      <aside className="attendance-sidebar">
        <div className="attendance-status">
          <div className="attendance-status__row">
            <span className="attendance-status__label">Admin</span>
            <span
              className={
                pingOk == null
                  ? 'attendance-status__pill'
                  : pingOk
                    ? 'attendance-status__pill ok'
                    : 'attendance-status__pill bad'
              }
              title={pingMsg}
            >
              {busy && pingOk == null ? '…' : pingOk ? 'API OK' : 'API ERR'}
            </span>
          </div>
          <div className="attendance-status__hint">
            Terminály, identifikátory a globální nastavení docházky.
          </div>
        </div>

        <nav className="attendance-nav" aria-label="Admin docházky">
          {NAV.map((entry, i) =>
            entry.kind === 'group' ? (
              <div key={`g-${entry.label}-${i}`} className="attendance-nav__group">
                {entry.label}
              </div>
            ) : (
              <button
                key={entry.key}
                type="button"
                className={
                  entry.key === tab ? 'attendance-nav__item active' : 'attendance-nav__item'
                }
                onClick={() => setTab(entry.key)}
              >
                {entry.label}
              </button>
            ),
          )}
        </nav>
      </aside>

      <div className="attendance-content">
        {tab === 'terminals' && (
          <section className="attendance-panel att-cat">
            <h2>{TAB_TITLES.terminals}</h2>
            <AttendanceTerminalsPanel busy={busy} setBusy={setBusy} showAlert={showAlert} />
          </section>
        )}

        {tab === 'credentials' && (
          <section className="attendance-panel att-cat">
            <h2>{TAB_TITLES.credentials}</h2>
            <AttendanceCredentialsPanel busy={busy} setBusy={setBusy} showAlert={showAlert} />
          </section>
        )}

        {tab === 'settings' && (
          <section className="attendance-panel">
            <h2>{TAB_TITLES.settings}</h2>
            <p className="attendance-muted" style={{ marginBottom: 12 }}>
              Globální pravidla docházky: stravenky, tolerance pozdního příchodu, noční, přesčasy,
              mzdové kódy a mapování veřejné IP → kód budovy u self-punchů.
            </p>
            <AttendanceHoursSettingsPanel showAlert={showAlert} embedded />
          </section>
        )}
      </div>
    </div>
  )
}
